import type { RefObject } from "react";

import { SendHorizontalIcon } from "lucide-react";

import { Button } from "@/components/button";

import type { ManagedFile } from "../../hooks/useFileUpload";

import { useChatMentionableUsers } from "../../hooks/useChatMentionableUsers";
import { ChatTextarea } from "./ChatTextArea";
import { FileUploadButton } from "./FileUploadButton";
import { UploadedFilesCollection } from "./UploadedFilesCollection";

interface ChatInputProps {
  canResize?: boolean;
  chatPanelRef?: RefObject<HTMLDivElement | null>;
  files: ManagedFile[];
  handleFilesSelected: (files: File[]) => void;
  handleSendMessage: () => void;
  isEditing?: boolean;
  isLoading: boolean;
  isSendDisabled?: boolean;
  isUploadDisabled?: boolean;
  messageContent: string;
  onCancelEdit?: () => void;
  onClearAll: () => void;
  onRemoveFile: (fileId: string) => void;
  onRetryUpload: (fileId: string) => void;
  requestId: number;
  setMessageContent: (content: string) => void;
}

export function ChatInput({
  canResize = false,
  chatPanelRef,
  files,
  handleFilesSelected,
  handleSendMessage,
  isLoading,
  isSendDisabled = false,
  isUploadDisabled = false,
  messageContent,
  onClearAll,
  onRemoveFile,
  onRetryUpload,
  requestId,
  setMessageContent,
}: ChatInputProps) {
  const { data: mentionableUsers = [] } = useChatMentionableUsers(requestId);

  const successfulUploads = files.filter((f) => f.status === "success");
  const isUploading = files.some((f) => f.status === "uploading");

  const canSendMessage =
    (messageContent.trim().length > 0 || successfulUploads.length > 0) &&
    !isLoading &&
    !isUploading &&
    !isSendDisabled;

  return (
    <div className="w-full @container">
      <UploadedFilesCollection
        files={files}
        onClearAll={onClearAll}
        onRemoveFile={onRemoveFile}
        onRetryUpload={onRetryUpload}
      />
      <div className="flex items-end gap-2">
        <div className="flex-1 max-w-[90%]">
          <ChatTextarea
            canResize={canResize}
            chatPanelRef={chatPanelRef}
            disabled={isLoading || isUploading}
            mentionSuggestions={mentionableUsers}
            onChange={setMessageContent}
            value={messageContent}
          />
        </div>

        <div className="flex flex-col gap-2">
          <FileUploadButton
            isUploadDisabled={isUploadDisabled}
            onFilesSelected={handleFilesSelected}
          />

          <div className="flex gap-2">
            <Button
              className="border border-border-primary text-button-secondary-fg w-[48px] h-[48px]"
              isDisabled={!canSendMessage}
              onPress={handleSendMessage}
              variant={canSendMessage ? "primary" : "secondary"}
            >
              <SendHorizontalIcon
                className="w-4 h-4"
                color={canSendMessage ? "white" : "gray"}
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
